import React, { useEffect, useState } from "react";

// ─────────────────────────────────────────────────────────────────────────────
// FennLoader — the animated Fennira mark
//
// The ears slide in, the chevron draws, the teal dot pops, then it all fades
// and repeats. The keyframes live in public/index.html (the `fl-*` classes), so
// the very first splash can play before any JavaScript has loaded; this file
// renders the same markup. Reduced-motion users get the still logo.
//
//   <FennMark size={40} />             inline, e.g. beside upload progress
//   <FennScreen />                     full-screen, matches the page-load splash
//   <FennOverlay show={!ready} />      full-screen that fades out when done
// ─────────────────────────────────────────────────────────────────────────────

const SAND = "#EFE3CC";
const TEAL = "#17A398";

export function FennMark({ size = 88, color = SAND }) {
  // Joins the page-wide animation clock (see index.html) rather than starting
  // from frame one, so replacing one loader with another doesn't make it jump.
  const [phase] = useState(() =>
    `-${Math.round((typeof performance !== "undefined" ? performance.now() : 0) % 2400)}ms`);
  return (
    <svg className="fl-mark" viewBox="20 4 200 224" width={size} height={Math.round(size * 1.12)}
      aria-hidden="true" style={{ "--fl-phase": phase }}>
      <polygon className="fl-ear-l" points="34,16 112,118 62,160"
        fill={color} stroke={color} strokeWidth="10" strokeLinejoin="round" />
      <polygon className="fl-ear-r" points="206,16 128,118 178,160"
        fill={color} stroke={color} strokeWidth="10" strokeLinejoin="round" />
      <path className="fl-chev" d="M82 174 L120 214 L158 174"
        fill="none" stroke={color} strokeWidth="14" strokeLinecap="round" strokeLinejoin="round" />
      <circle className="fl-halo" cx="120" cy="92" r="7" fill="none" stroke={TEAL} strokeWidth="2" />
      <circle className="fl-dot" cx="120" cy="92" r="7" fill={TEAL} />
    </svg>
  );
}

// Same size and place as the splash in index.html, so moving from the page
// load to the sign-in check to the Studio's first data looks like one animation.
export function FennScreen({ label = "Loading Fennira", out = false }) {
  return (
    <div className={`fl-screen${out ? " fl-out" : ""}`} role="status" aria-label={label}>
      <FennMark size={88} />
    </div>
  );
}

/**
 * Full-screen loader that fades out (instead of vanishing) once `show` goes
 * false. `maxMs` is a safety net: a request that never answers must not leave
 * the user staring at the logo.
 */
export function FennOverlay({ show, label, maxMs = 8000 }) {
  const [mounted, setMounted] = useState(show);
  const [out, setOut] = useState(false);
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    if (!show) return undefined;
    const t = setTimeout(() => setTimedOut(true), maxMs);
    return () => clearTimeout(t);
  }, [show, maxMs]);

  const visible = show && !timedOut;

  useEffect(() => {
    if (visible) { setMounted(true); setOut(false); return undefined; }
    setOut(true);
    const t = setTimeout(() => setMounted(false), 380);
    return () => clearTimeout(t);
  }, [visible]);

  if (!mounted) return null;
  return <FennScreen label={label} out={out} />;
}
